<template>
  <div class="tw:flex tw:flex-col">
    <div
      class="tw:flex tw:flex-col tw:rounded-t-[22px]"
      :class="{
        'ai-background-gradient': gradientHeader,
        'tw:min-h-[192px]': gradientHeader && !aiModalMinimise,
      }"
    >
      <div class="tw:flex tw:p-4 tw:overflow-hidden tw:gap-2">
        <div class="tw:flex tw:items-center">
          <slot name="toolbarLeft">
            <BaseButton
              v-if="!hideHomeButton"
              circle
              data-testid="home-button"
              flat
              icon="chevron-left"
              :label="homeButtonLabel"
              new-design
              new-icon
              @click="emit('home')"
            />
          </slot>
        </div>
        <div
          class="tw:flex tw:flex-1 tw:items-center tw:justify-center tw:overflow-hidden"
        >
          <slot name="toolbarCenter">
            <EllipseContent
              v-if="title"
              class="tw:font-bold"
              data-testid="title"
            >
              {{ title }}
            </EllipseContent>
          </slot>
        </div>
        <div class="tw:flex tw:items-center tw:justify-end">
          <slot name="toolbarPrependRight" />
          <template v-if="isDesktop">
            <div>
              <BaseButton
                circle
                data-testid="position-button"
                flat
                :icon="positionIcon"
                new-design
                new-icon
                @click="updatePosition"
              />
              <StreemTooltip>
                {{ positionTooltip }}
              </StreemTooltip>
            </div>
            <div>
              <BaseButton
                circle
                data-testid="size-button"
                flat
                :icon="sizeIcon"
                new-design
                new-icon
                @click="updateSize"
              />
              <StreemTooltip>{{ sizeTooltip }}</StreemTooltip>
            </div>
          </template>
          <BaseButton
            circle
            data-testid="close-button"
            flat
            icon="close"
            new-design
            new-icon
            @click="emit('close')"
          />
        </div>
      </div>
      <slot name="header" />
    </div>
    <BaseSeparator />

    <div
      ref="bodyRef"
      class="ai-scrollbar tw:flex-1"
      :class="bodyRefClass"
    >
      <slot />
    </div>

    <slot name="footer" />
  </div>
</template>

<script setup lang="ts">
import { useMutationObserver } from "@vueuse/core";
import { debounce } from "lodash-es";
import { storeToRefs } from "pinia";
import {
  type ComputedRef,
  computed,
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  useTemplateRef,
} from "vue";

import { getLocaleText } from "shared/boot/i18n";
import {
  BaseButton,
  BaseSeparator,
  EllipseContent,
  StreemTooltip,
} from "shared/components/base";
import useAIStore from "shared/stores/ai";
import type { Nullable } from "shared/types";

const aiStore = useAIStore();
const { aiModalMinimise, aiModalLeft } = storeToRefs(aiStore);

const emit = defineEmits(["min", "close", "home"]);

interface AIBasePanelProps {
  autoScroll?: boolean;
  bodyClass?: string;
  gradientHeader?: boolean;
  hideHomeButton?: boolean;
  title?: string;
}

const props = defineProps<AIBasePanelProps>();

const isDesktop = inject<boolean>("isDesktop", false);

const homeButtonLabel = computed<string>(() =>
  isDesktop ? getLocaleText("ai_base_panel.home") : ""
);

const sizeIcon = computed(() => (aiModalMinimise.value ? "plus" : "minus"));

const sizeTooltip = computed(() =>
  aiModalMinimise.value
    ? getLocaleText("ai_base_panel.maximise")
    : getLocaleText("ai_base_panel.minimise")
);

const positionIcon = computed(() =>
  aiModalLeft.value ? "move-right" : "move-left"
);

const positionTooltip = computed<string>(() =>
  aiModalLeft.value
    ? getLocaleText("ai_base_panel.move_right")
    : getLocaleText("ai_base_panel.move_left")
);

const bodyRef = useTemplateRef("bodyRef");

const bodyRefClass = computed(() => {
  if (props.bodyClass) {
    return props.bodyClass;
  }

  return "tw:overflow-auto tw:scroll-smooth tw:p-4";
});

function updateSize() {
  aiModalMinimise.value = !aiModalMinimise.value;
}

function updatePosition() {
  aiModalLeft.value = !aiModalLeft.value;
}

async function scrollToEndOfBody() {
  await nextTick();

  if (bodyRef.value && bodyRef.value.scrollTop !== bodyRef.value.scrollHeight) {
    bodyRef.value.scrollTop = bodyRef.value.scrollHeight;
  }
}

function scrollToTop() {
  if (bodyRef.value) {
    bodyRef.value.scrollTop = 0;
  }
}

const debounceScroll = debounce(scrollToEndOfBody, 50);

interface MutationObserver {
  isSupported: ComputedRef<boolean>;
  stop: () => void;
  takeRecords: () => MutationRecord[] | undefined;
}

let mutationObserver: Nullable<MutationObserver> = null;

onMounted(() => {
  if (!props.autoScroll) return;

  mutationObserver = useMutationObserver(
    bodyRef,
    (mutations) => {
      const mutation: MutationRecord = mutations[0];

      const { nodeName, textContent } = mutation.removedNodes[0] || {};

      if (nodeName === "#text" && !textContent) return;
      if (nodeName === "#comment") return;

      debounceScroll();
    },
    {
      childList: true,
      subtree: true,
    }
  );
});

onBeforeUnmount(() => {
  mutationObserver?.stop();
});

defineExpose({
  scrollToTop,
});
</script>

<style scoped lang="scss">
@import "shared/css/components/ai.scss";
</style>
